import { useState } from 'react';
import { MessageSquarePlus, X, Send, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { notify } from '../store/notificationStore';
import { useAuthStore } from '../store/authStore';
import logger from '../lib/logger';

type FeedbackCategory = 'bug' | 'idea' | 'other';

const CATEGORY_OPTIONS: { value: FeedbackCategory; label: string }[] = [
  { value: 'bug', label: 'Bug' },
  { value: 'idea', label: 'Idea' },
  { value: 'other', label: 'Other' },
];

/**
 * Floating in-the-moment feedback widget. Reports post to /api/v1/feedback/,
 * which persists them and opens a triaged GitHub issue so bugs flow straight
 * into the auto-remediation pipeline.
 */
export function FeedbackWidget() {
  const { user } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState<FeedbackCategory>('bug');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await api.post('/feedback/', {
        category,
        message: message.trim(),
        email: user?.email || email.trim(),
        page_url: window.location.href.slice(0, 500),
        user_agent: navigator.userAgent.slice(0, 500),
      });
      notify.success(
        'Thanks for the report!',
        category === 'bug'
          ? 'It has been queued for a fix — we look at every one.'
          : 'It has been sent to the team.'
      );
      setMessage('');
      setCategory('bug');
      setIsOpen(false);
    } catch (err) {
      logger.error('Failed to submit feedback:', err);
      notify.error('Could not send your report', 'Please try again in a moment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-2 print:hidden">
      {isOpen && (
        <form
          onSubmit={handleSubmit}
          className="w-80 rounded-lg border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-900 p-4 shadow-xl"
          data-testid="feedback-panel"
        >
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">
              Report a problem
            </h3>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              aria-label="Close feedback"
              className="rounded p-1 text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-800 hover:text-secondary-600 dark:hover:text-secondary-300"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mb-3 flex gap-1" role="radiogroup" aria-label="Feedback category">
            {CATEGORY_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={category === option.value}
                onClick={() => setCategory(option.value)}
                className={`flex-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors ${
                  category === option.value
                    ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'border-secondary-200 dark:border-secondary-700 text-secondary-600 dark:text-secondary-400 hover:border-secondary-300'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            maxLength={5000}
            required
            placeholder={
              category === 'bug'
                ? 'What went wrong? What did you expect to happen?'
                : 'Tell us what you have in mind…'
            }
            className="mb-3 w-full resize-none rounded-md border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800 px-3 py-2 text-sm text-secondary-900 dark:text-white placeholder:text-secondary-400 focus:border-blue-500 focus:outline-none"
            data-testid="feedback-message"
          />

          {!user && (
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email (optional, for follow-up)"
              className="mb-3 w-full rounded-md border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800 px-3 py-2 text-sm text-secondary-900 dark:text-white placeholder:text-secondary-400 focus:border-blue-500 focus:outline-none"
              data-testid="feedback-email"
            />
          )}

          <button
            type="submit"
            disabled={isSubmitting || !message.trim()}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="feedback-submit"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {isSubmitting ? 'Sending…' : 'Send report'}
          </button>

          <p className="mt-2 text-[11px] leading-tight text-secondary-400 dark:text-secondary-500">
            The current page URL is included so we can reproduce the problem.
          </p>
        </form>
      )}

      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-label={isOpen ? 'Close feedback widget' : 'Report a problem'}
        title="Report a problem"
        className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition-transform hover:scale-105 hover:bg-blue-700"
        data-testid="feedback-toggle"
      >
        {isOpen ? <X className="h-5 w-5" /> : <MessageSquarePlus className="h-5 w-5" />}
      </button>
    </div>
  );
}
